import { useEffect, useState } from "react";
import { AccessibilityInfo } from "react-native";

let reduced = false;
let osSubscription: { remove: () => void; } | null = null;
const subscribers = new Set<(value: boolean) => void>();

function publish(value: boolean) {
  if (value === reduced) return;
  reduced = value;
  for (const notify of [...subscribers]) notify(value);
}

function subscribe(notify: (value: boolean) => void) {
  subscribers.add(notify);
  if (subscribers.size === 1) {
    osSubscription = AccessibilityInfo.addEventListener("reduceMotionChanged", publish);
  }
  // Re-read on every mount, not only the first. `reduceMotionChanged` covers changes made while
  // something is mounted; a setting changed while the app was backgrounded, or before anything
  // subscribed, only shows up on a read.
  void AccessibilityInfo.isReduceMotionEnabled().then(publish);
  return () => {
    subscribers.delete(notify);
    if (subscribers.size === 0) {
      osSubscription?.remove();
      osSubscription = null;
    }
  };
}

/**
 * Tracks the OS "Reduce Motion" accessibility setting, initially and as it changes. Screens use it
 * to replace slide transitions with a crossfade: honoring prefers-reduced-motion is a stated
 * accessibility requirement, and a full slide can nauseate motion-sensitive users.
 *
 * One OS listener and one remembered value serve the whole app. `CardArt`, the art primitive under
 * every list row and tile, uses this hook, so per-component state would register and remove a
 * native `reduceMotionChanged` subscription per visible image. Each new mount would also start at
 * `false` until its own async read resolved, showing the crossfade to a motion-sensitive user on
 * every cell scrolled into view. Keeping the last known value at module scope lets a newly mounted
 * component render the right answer on its first frame.
 */
export function useReducedMotion(): boolean {
  const [value, setValue] = useState(reduced);
  useEffect(() => {
    // The shared value may have changed between this component's render and its effect.
    setValue(reduced);
    return subscribe(setValue);
  }, []);
  return value;
}
